import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Heading, Spinner, VStack, Presence } from '@chakra-ui/react';
import { Paragraph } from '@src/components/sco/Paragraph';
import { SignupRequestEditStep } from '@src/components/sco/Requests/SignupRequestBuilder/EditStep';
import { SignupRequestPaymentStep } from '@src/components/sco/Requests/SignupRequestBuilder/PaymentStep';
import { SignupRequestPreviewStep } from '@src/components/sco/Requests/SignupRequestBuilder/PreviewStep';
import { SignupRequestPriorityStep } from '@src/components/sco/Requests/SignupRequestBuilder/PriorityStep';
import { useSignupRequestQuery, useUpsertSignupRequestMutation } from '@src/components/sco/Requests/SignupRequestBuilder/helpers';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { groupRequestTypes } from '@src/constants/group-request-types';
import { ScoSignupRequest, UpsertSignupRequestInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useDebounce } from '@uidotdev/usehooks';
import { useRouter } from 'next/navigation';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useMemo, useState } from 'react';
export type SignupRequestBuilderProps = {
  containerBoxProps?: BoxProps;
  isFullScreen?: boolean;
  funnelIdentifier?: string;
};
export const SignupRequestBuilder: React.FC<SignupRequestBuilderProps> = props => {
  const $ = _c(76);
  const {
    currentUser
  } = useCurrentUser();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const router = useRouter();
  const isFullScreen = props.isFullScreen || false;
  const [signupRequestId, setSignupRequestId] = useQueryState("signupRequestId", parseAsString);
  const t0 = !signupRequestId;
  let t1;
  if ($[0] !== signupRequestId || $[1] !== t0) {
    t1 = {
      variables: {
        skip: t0,
        signupRequestId
      }
    };
    $[0] = signupRequestId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useSignupRequestQuery(t1);
  const [upsertSignupRequest] = useUpsertSignupRequestMutation();
  const saveSignupRequest = async (t2, t3) => {
    const next = t2 === undefined ? null : t2;
    const additionalInput = t3 === undefined ? {} : t3;
    const result = await upsertSignupRequest({
      variables: {
        input: {
          funnelIdentifier: props.funnelIdentifier,
          bodyAsMarkdown: signupRequest.bodyAsMarkdown,
          companyElevatorPitch: signupRequest.companyElevatorPitch,
          fundingGoalInDollars: signupRequest.fundingGoalInDollars,
          groupRequestTypeKey: signupRequest.groupRequestTypeKey,
          investorIndustryTagTitles: signupRequest.investorIndustryTagTitles,
          pitchDeckUrl: signupRequest.pitchDeckUrl,
          signupRequestId: signupRequest.signupRequestId,
          skillTagTitles: signupRequest.skillTagTitles,
          ...additionalInput
        }
      }
    });
    const returnedSignupRequest = result.data.upsertSignupRequest.signupRequest;
    if (returnedSignupRequest) {
      if (signupRequestId !== returnedSignupRequest.signupRequestId) {
        setSignupRequestId(returnedSignupRequest.signupRequestId);
      }
      setSignupRequest(returnedSignupRequest);
    }
    if (next) {
      next(returnedSignupRequest);
    }
  };
  let t4;
  if ($[3] !== signupRequestId) {
    t4 = {
      signupRequestId
    };
    $[3] = signupRequestId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [t5, t6] = useState(t4);
  const signupRequest = t5;
  const setSignupRequest = t6;
  let t7;
  if ($[5] !== setSignupRequest) {
    t7 = t8 => {
      const values = t8 === undefined ? {} : t8;
      setSignupRequest(prev => ({
        ...prev,
        ...values
      }));
    };
    $[5] = setSignupRequest;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const updateSignupRequest = t7;
  signupRequest;
  let t8;
  if ($[7] !== signupRequest.groupRequestTypeKey) {
    t8 = groupRequestTypes.find(t => t.key === signupRequest.groupRequestTypeKey);
    $[7] = signupRequest.groupRequestTypeKey;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const groupRequestType = t8;
  let t9;
  if ($[9] !== query.data || $[10] !== setSignupRequest) {
    t9 = () => {
      if (query.data?.scoSignupRequest) {
        setSignupRequest(query.data.scoSignupRequest);
      }
    };
    $[9] = query.data;
    $[10] = setSignupRequest;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = query.data?.scoSignupRequest?.signupRequestId;
  let t11;
  if ($[12] !== t10) {
    t11 = [t10];
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  useEffect(t9, t11);
  let t12;
  if ($[14] !== query.error || $[15] !== setSignupRequestId) {
    t12 = () => {
      if (query.error) {
        setSignupRequestId(null);
      }
    };
    $[14] = query.error;
    $[15] = setSignupRequestId;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== query.error) {
    t13 = [query.error];
    $[17] = query.error;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  useEffect(t12, t13);
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = parseAsString.withDefault("edit");
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const [showStep, setShowStep] = useQueryState("step", t14);
  const debouncedShowStep = useDebounce(showStep, 500);
  let t15;
  if ($[20] !== getBreakpointValue) {
    t15 = getBreakpointValue(["md", "md", "lg", "xl", "2xl"]);
    $[20] = getBreakpointValue;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const borderRadius = t15;
  let t16;
  if ($[22] !== currentUser || $[23] !== router || $[24] !== saveSignupRequest || $[25] !== setShowStep || $[26] !== showStep || $[27] !== signupRequest) {
    t16 = () => {
      if (showStep === "commit" && signupRequest?.signupRequestId) {
        const isRealUser = !!currentUser?.isRegistered && !!currentUser?.userId && currentUser.userId !== "00000000-0000-0000-0000-000000000000";
        if (isRealUser) {
          saveSignupRequest(returnedSignupRequest_0 => {
            router.push(`/requests/manage/${returnedSignupRequest_0.groupRequestId}`);
          }, {
            commitToGroupRequest: true
          });
        } else {
          setShowStep("edit");
        }
      }
    };
    $[22] = currentUser;
    $[23] = router;
    $[24] = saveSignupRequest;
    $[25] = setShowStep;
    $[26] = showStep;
    $[27] = signupRequest;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = signupRequest?.signupRequestId;
  let t18;
  if ($[29] !== debouncedShowStep || $[30] !== signupRequestId || $[31] !== t17) {
    t18 = [debouncedShowStep, t17, signupRequestId];
    $[29] = debouncedShowStep;
    $[30] = signupRequestId;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  useEffect(t16, t18);
  let t19;
  if ($[33] !== borderRadius || $[34] !== groupRequestType || $[35] !== isFullScreen || $[36] !== saveSignupRequest || $[37] !== setShowStep || $[38] !== showStep || $[39] !== signupRequest || $[40] !== updateSignupRequest) {
    t19 = showStep === "edit" && !isFullScreen && <SignupRequestEditStep borderRadius={borderRadius} groupRequestType={groupRequestType} saveSignupRequest={saveSignupRequest} setShowStep={setShowStep} signupRequest={signupRequest} updateSignupRequest={updateSignupRequest} />;
    $[33] = borderRadius;
    $[34] = groupRequestType;
    $[35] = isFullScreen;
    $[36] = saveSignupRequest;
    $[37] = setShowStep;
    $[38] = showStep;
    $[39] = signupRequest;
    $[40] = updateSignupRequest;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== borderRadius || $[43] !== groupRequestType || $[44] !== saveSignupRequest || $[45] !== setShowStep || $[46] !== showStep || $[47] !== signupRequest || $[48] !== updateSignupRequest) {
    t20 = showStep === "priority" && <SignupRequestPriorityStep borderRadius={borderRadius} groupRequestType={groupRequestType} saveSignupRequest={saveSignupRequest} setShowStep={setShowStep} signupRequest={signupRequest} updateSignupRequest={updateSignupRequest} />;
    $[42] = borderRadius;
    $[43] = groupRequestType;
    $[44] = saveSignupRequest;
    $[45] = setShowStep;
    $[46] = showStep;
    $[47] = signupRequest;
    $[48] = updateSignupRequest;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== borderRadius || $[51] !== groupRequestType || $[52] !== saveSignupRequest || $[53] !== setShowStep || $[54] !== showStep || $[55] !== signupRequest || $[56] !== updateSignupRequest) {
    t21 = showStep === "preview" && <SignupRequestPreviewStep borderRadius={borderRadius} groupRequestType={groupRequestType} saveSignupRequest={saveSignupRequest} setShowStep={setShowStep} signupRequest={signupRequest} updateSignupRequest={updateSignupRequest} />;
    $[50] = borderRadius;
    $[51] = groupRequestType;
    $[52] = saveSignupRequest;
    $[53] = setShowStep;
    $[54] = showStep;
    $[55] = signupRequest;
    $[56] = updateSignupRequest;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== borderRadius || $[59] !== groupRequestType || $[60] !== saveSignupRequest || $[61] !== setShowStep || $[62] !== showStep || $[63] !== signupRequest || $[64] !== updateSignupRequest) {
    t22 = showStep === "payment" && <SignupRequestPaymentStep borderRadius={borderRadius} groupRequestType={groupRequestType} saveSignupRequest={saveSignupRequest} setShowStep={setShowStep} signupRequest={signupRequest} updateSignupRequest={updateSignupRequest} />;
    $[58] = borderRadius;
    $[59] = groupRequestType;
    $[60] = saveSignupRequest;
    $[61] = setShowStep;
    $[62] = showStep;
    $[63] = signupRequest;
    $[64] = updateSignupRequest;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== showStep) {
    t23 = (showStep === "commit" || showStep === "committing") && <VStack paddingY={24}><Spinner boxSize={10} marginBottom={4} /><Heading lineHeight={1.3} textAlign="center">We're setting up your goal.<br />One moment please...</Heading><Presence animationDuration="moderate" present={true} style={{
        animationDelay: "2s"
      }} animationName={{
        _open: "slide-from-bottom, fade-in",
        _closed: "slide-to-bottom, fade-out"
      }}><Paragraph>Analyzing your company and goals...</Paragraph></Presence><Presence animationDuration="moderate" present={true} style={{
        animationDelay: "5s"
      }} animationName={{
        _open: "slide-from-bottom, fade-in",
        _closed: "slide-to-bottom, fade-out"
      }}><Paragraph>Building your Game Plan...</Paragraph></Presence><Presence animationDuration="moderate" present={true} style={{
        animationDelay: "8s"
      }} animationName={{
        _open: "slide-from-bottom, fade-in",
        _closed: "slide-to-bottom, fade-out"
      }}><Paragraph>Finding matches...</Paragraph></Presence><Presence animationDuration="moderate" present={true} style={{
        animationDelay: "11s"
      }} animationName={{
        _open: "slide-from-bottom, fade-in",
        _closed: "slide-to-bottom, fade-out"
      }}><Paragraph>Finishing up...</Paragraph></Presence></VStack>;
    $[66] = showStep;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== borderRadius || $[69] !== props.containerBoxProps || $[70] !== t19 || $[71] !== t20 || $[72] !== t21 || $[73] !== t22 || $[74] !== t23) {
    t24 = <><Box bgColor="colorPalette.container" border="1px solid" borderColor="colorPalette.border" borderRadius={borderRadius} boxShadow="md" width="full" {...props.containerBoxProps}>{t19}{t20}{t21}{t22}{t23}</Box></>;
    $[68] = borderRadius;
    $[69] = props.containerBoxProps;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  return t24;
};